import { logUsage } from 'owa-analytics';
import { trace } from 'owa-trace';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { timestamp } from 'owa-datetime';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getComposeTypeForHostItem } from 'owa-addins-adapters';
import type { MailboxInfo } from 'owa-client-types';

export type EventState = 'Triggered' | 'Completed' | 'Failed' | 'InvokeAddinFunction';

type EventCounts = {
    T: number; // Triggered Count - Representing it as T to save telemetry log length as 1000 is max misc data to be logged
    C: number; // Completed Count - Representing it as C to save telemetry log length as 1000 is max misc data to be logged
    F: number; // Failed Count - Representing it as F to save telemetry log length as 1000 is max misc data to be logged
    I: number; // InvokeAddinFunction Count - Applicable only for Events invoking function define in Addin Code
    s?: 0 | 1; // 1 if this event was triggered via SDK path (0/1 saves space vs boolean in JSON)
};

type ControlEventCounts = Record<string, EventCounts>; // Mapping event names to counts

type AddinProperties = Record<string, string>;

// New types for session event tracking
type SessionEventEntry = {
    evt: string; // eventName - shortened for telemetry size
    st: string; // eventState - shortened to single letter (T/C/F/I)
    seq: number; // sequenceId - shortened for telemetry size
    dur?: number; // duration - milliseconds (for completed/failed/InvokeAddinFunction)
    ts: string; // timestamp - rounded performance.now() value in milliseconds (e.g., "1235")
    cid?: string; // correlationId - links back to SDK compose surface event (e.g., EVT_OnComposeNew)
    s?: 0 | 1; // 1 if this event took SDK path (0/1 saves space vs boolean in JSON)
    sdur?: number; // SDK bridge duration in ms (only on Triggered entries for SDK events)
};

type EventTiming = {
    startTime: number; // performance.now() when triggered
    sequenceId: number; // sequence ID of the triggered event
    sdur?: number; // SDK bridge duration computed at Triggered time, logged on Completed/Failed
};

type SessionTracking = {
    sessionStartTime: number;
    globalSequenceCounter: number;
    eventTimings: Record<string, EventTiming[]>; // eventName -> array of triggered timings
    eventHistory: SessionEventEntry[];
};

type Properties = {
    addinProperties: AddinProperties;
    controlEventCounts: ControlEventCounts;
    sessionTracking: SessionTracking;
    mailboxInfo?: MailboxInfo;
    composeType?: string; // Compose surface type (e.g. New/Reply/Forward) logged as a top-level field
};

const MAX_TRACKED_CONTROLS = 100;
const MAX_CHUNKS_LIMIT = 50;

export type Tracker = {
    trackEvent: (
        controlId: string,
        eventName: string,
        eventState: EventState,
        correlationId?: string,
        isSdkPath?: boolean,
        sdkEntryTimestamp?: number
    ) => void;
    setAddinProperties: (controlId: string, addinProperties: AddinProperties) => void;
    setComposeType: (controlId: string, hostItemIndex: string) => void;
    getEventCounts: (controlId: string, eventName: string) => EventCounts | undefined;
    getAllEventCountsForControl: (controlId: string) => Properties | undefined;
    getAllEventCounts: () => Record<string, Properties>;
    resetAllEventCountsForControl: (controlId: string) => void;
    resetCounts: () => void;
    endAddinTracking: (
        controlId: string,
        eventName: string,
        eventState: EventState,
        mailboxInfo?: MailboxInfo
    ) => void;
    getSessionEvents: (controlId: string) => SessionEventEntry[];
    getSessionElapsedTime: (controlId: string) => number;
};

export function createTracker(): Tracker {
    const state: Record<string, Properties> = {};
    // Maintain insertion order using an array - oldest sessions are at the front
    const sessionOrder: string[] = [];

    // Helper function to enforce max records limit - O(1) operation
    const enforceMaxRecordsLimit = (): void => {
        if (sessionOrder.length >= MAX_TRACKED_CONTROLS) {
            // Get the oldest session (first in the array) - O(1)
            const oldestControlId = sessionOrder[0];
            if (oldestControlId && state[oldestControlId]) {
                trace.warn(
                    `AddinTracker - Max records limit (${MAX_TRACKED_CONTROLS}) reached. Cleaning up oldest session: ${oldestControlId}`
                );

                logUsage(
                    'ExtAddinTrackerMaxLimitReached',
                    getMailboxInfoForControl(oldestControlId),
                    {
                        event: 'MaxLimitReached',
                        controlId: oldestControlId,
                        totalTrackedControls: sessionOrder.length,
                    }
                );
                // Log the session before cleanup
                logSessionEvents(oldestControlId);
                logAllEventForControlId(oldestControlId);
                delete state[oldestControlId];
                // Remove from order tracking array - O(1) since it's always at index 0
                sessionOrder.shift();
            }
        }
    };

    // Helper function to ensure state exists for a controlId - prevents duplicate initialization
    const ensureStateExists = (controlId: string): void => {
        if (!state[controlId]) {
            enforceMaxRecordsLimit();
            state[controlId] = {
                addinProperties: {},
                controlEventCounts: {},
                sessionTracking: {
                    sessionStartTime: performance.now(),
                    globalSequenceCounter: 0,
                    eventTimings: {},
                    eventHistory: [],
                },
            };
            // Track insertion order - O(1), only happens once per controlId
            sessionOrder.push(controlId);
        }
    };

    // Helper function to convert EventState to shortened form for telemetry
    const getShortEventState = (eventState: EventState): string => {
        switch (eventState) {
            case 'Triggered':
                return 'T';
            case 'Completed':
                return 'C';
            case 'Failed':
                return 'F';
            case 'InvokeAddinFunction':
                return 'I';
            default:
                return eventState;
        }
    };

    const setAddinProperties = (controlId: string, addinProperties: AddinProperties) => {
        ensureStateExists(controlId);

        state[controlId].addinProperties = {
            ...state[controlId].addinProperties,
            ...addinProperties,
        };
    };

    // Resolves the compose surface type from the host item's adapter and stores it,
    // logged as a top-level field on ExtAddinTracker and ExtAddinSessionHistory.
    const setComposeType = (controlId: string, hostItemIndex: string) => {
        ensureStateExists(controlId);
        const composeType = getComposeTypeForHostItem(hostItemIndex);
        if (composeType !== undefined) {
            state[controlId].composeType = composeType;
        }
    };

    const trackEvent = (
        controlId: string,
        eventName: string,
        eventState: EventState,
        correlationId?: string,
        isSdkPath?: boolean,
        sdkEntryTimestamp?: number
    ) => {
        ensureStateExists(controlId);

        if (!state[controlId].controlEventCounts[eventName]) {
            state[controlId].controlEventCounts[eventName] = {
                T: 0,
                C: 0,
                F: 0,
                I: 0,
            };
        }

        // Existing count tracking (unchanged)
        if (eventState === 'Triggered') {
            state[controlId].controlEventCounts[eventName].T++;
        } else if (eventState === 'Completed') {
            state[controlId].controlEventCounts[eventName].C++;
        } else if (eventState === 'Failed') {
            state[controlId].controlEventCounts[eventName].F++;
        } else if (eventState === 'InvokeAddinFunction') {
            state[controlId].controlEventCounts[eventName].I++;
        }

        if (isSdkPath) {
            state[controlId].controlEventCounts[eventName].s = 1;
        }

        trackSessionEvent(
            state[controlId].sessionTracking,
            eventName,
            eventState,
            correlationId,
            isSdkPath,
            sdkEntryTimestamp
        );
    };

    // New session tracking logic
    const trackSessionEvent = (
        sessionTracking: SessionTracking,
        eventName: string,
        eventState: EventState,
        correlationId?: string,
        isSdkPath?: boolean,
        sdkEntryTimestamp?: number
    ) => {
        const currentTime = performance.now();

        // Increment sequence counter with overflow protection
        // JavaScript numbers are safe up to Number.MAX_SAFE_INTEGER (2^53 - 1)
        // For defense-in-depth, reset if we approach the limit (though extremely unlikely in practice)
        if (sessionTracking.globalSequenceCounter >= Number.MAX_SAFE_INTEGER - 1) {
            trace.warn(
                `AddinTracker - Sequence counter approaching max safe integer (${Number.MAX_SAFE_INTEGER}). Resetting to 0.`
            );
            sessionTracking.globalSequenceCounter = 0;
        }

        const sequenceId = ++sessionTracking.globalSequenceCounter;

        if (eventState === 'Triggered') {
            // Initialize event timings array if needed
            if (!sessionTracking.eventTimings[eventName]) {
                sessionTracking.eventTimings[eventName] = [];
            }

            // For SDK-path events, backdate startTime by the elapsed time since SDK entry
            // so dur captures full SDK entry → completion latency.
            // Clamp delta to >= 0 to guard against clock skew or bad input.
            const delta = sdkEntryTimestamp ? Math.max(0, timestamp() - sdkEntryTimestamp) : 0;
            const startTime = currentTime - delta;

            // SDK bridge duration: time from SDK entry to addins pipeline trigger
            // Logged on Completed/Failed entry alongside dur
            const sdur = delta > 0 ? Math.round(delta * 1000) / 1000 : undefined;

            // Store the triggered event timing for duration + sdur calculation later
            sessionTracking.eventTimings[eventName].push({
                startTime,
                sequenceId,
                sdur,
            });

            // Add to event history (sdur deferred to Completed/Failed entry)
            sessionTracking.eventHistory.push({
                evt: eventName,
                st: getShortEventState(eventState),
                seq: sequenceId,
                ts: Math.round(currentTime).toString(),
                ...(correlationId ? { cid: correlationId } : {}),
                ...(isSdkPath ? { s: 1 } : {}),
            });
        } else if (eventState === 'Completed' || eventState === 'Failed') {
            // Find the most recent triggered event for this eventName to calculate duration
            const eventTimings = sessionTracking.eventTimings[eventName];
            let duration: number | undefined;
            let sdur: number | undefined;

            if (eventTimings && eventTimings.length > 0) {
                const triggeredEvent = eventTimings.shift();
                if (triggeredEvent) {
                    duration = Math.max(
                        0,
                        Math.round((currentTime - triggeredEvent.startTime) * 1000) / 1000
                    );
                    sdur = triggeredEvent.sdur;
                }
            }

            sessionTracking.eventHistory.push({
                evt: eventName,
                st: getShortEventState(eventState),
                seq: sequenceId,
                dur: duration,
                ts: Math.round(currentTime).toString(),
                ...(correlationId ? { cid: correlationId } : {}),
                ...(sdur !== undefined ? { sdur } : {}),
            });
        } else if (eventState === 'InvokeAddinFunction') {
            // InvokeAddinFunction can also have duration - calculate from most recent triggered event
            // But DON'T remove the event from the list as it should remain for the actual Completed event
            let duration: number | undefined;

            const eventTimings = sessionTracking.eventTimings[eventName];
            if (eventTimings && eventTimings.length > 0) {
                const triggeredEvent = eventTimings[0]; // Access WITHOUT removing
                if (triggeredEvent) {
                    duration = Math.max(
                        0,
                        Math.round((currentTime - triggeredEvent.startTime) * 1000) / 1000
                    );
                }
            }

            sessionTracking.eventHistory.push({
                evt: eventName,
                st: getShortEventState(eventState),
                seq: sequenceId,
                dur: duration,
                ts: Math.round(currentTime).toString(),
                ...(correlationId ? { cid: correlationId } : {}),
            });
        }
    };

    const getEventCounts = (controlId: string, eventName: string): EventCounts | undefined => {
        return state[controlId]?.controlEventCounts[eventName];
    };

    const getAllEventCountsForControl = (controlId: string): Properties | undefined => {
        return state[controlId];
    };

    const getAllEventCounts = (): Record<string, Properties> => {
        return state;
    };

    const resetCounts = () => {
        for (const controlId of Object.keys(state)) {
            if (Object.prototype.hasOwnProperty.call(state, controlId)) {
                delete state[controlId];
            }
        }
        // Clear the session order tracking
        sessionOrder.length = 0;
    };

    const resetAllEventCountsForControl = (controlId: string) => {
        if (state[controlId]) {
            state[controlId].controlEventCounts = {};
            state[controlId].addinProperties = {};
            state[controlId].sessionTracking = {
                sessionStartTime: performance.now(),
                globalSequenceCounter: 0,
                eventTimings: {},
                eventHistory: [],
            };
        }
        delete state[controlId];
        // Remove from session order tracking - O(n) but only happens on explicit cleanup
        const index = sessionOrder.indexOf(controlId);
        if (index !== -1) {
            sessionOrder.splice(index, 1);
        }
    };

    const getMailboxInfoForControl = (controlId: string): MailboxInfo => {
        return state[controlId]?.mailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
    };

    const logAllEventForControlId = (controlId: string) => {
        const controlState = state[controlId];
        if (!controlState) {
            return;
        }

        // Create log data without sessionTracking to avoid duplication with ExtAddinSessionHistory
        const legacyTrackerData = {
            addinProperties: controlState.addinProperties,
            controlEventCounts: controlState.controlEventCounts,
            composeType: controlState.composeType,
        };

        trace.info(
            `ExtAddinTracker - Events for controlId:${controlId} - ${JSON.stringify(
                legacyTrackerData
            )}`
        );

        logUsage('ExtAddinTracker', getMailboxInfoForControl(controlId), {
            controlId,
            composeType: controlState.composeType,
            /* eslint-disable-next-line owa-custom-rules/telemetry-no-nested-objects -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE!, replace this line with a justification or fix the issue.
             *	> Do not call JSON.stringify(...) in logUsage telemetry data. Log flat, scalar custom-data fields instead of stringified or nested data; nested blobs force expensive, bug-prone recursive JSON parsing in the downstream pipeline. */
            events: JSON.stringify(legacyTrackerData),
        });
    };

    const logSessionEvents = (controlId: string) => {
        const controlState = state[controlId];
        if (!controlState) {
            return;
        }

        // Skip logging session events for Viva Insights - Because of high volume of Session History
        // As part of this initiative we are interested to track 3P Addins, so ignoring Viva Insights here.
        const addinName = controlState.addinProperties['AddinName'];
        if (addinName === 'Viva Insights') {
            trace.info(
                `ExtAddinSessionHistory - Skipping session event logging for Viva Insights (controlId: ${controlId})`
            );
            return;
        }

        const sessionTracking = controlState.sessionTracking;
        const sessionElapsedTime = performance.now() - sessionTracking.sessionStartTime;
        const sessionStartTime = new Date(Date.now() - sessionElapsedTime).toISOString();

        // Create the structured telemetry data for trace logging (complete data)
        const sessionEventData = {
            controlId,
            addinProperties: controlState.addinProperties,
            totalEventsInSession: sessionTracking.eventHistory.length,
            sessionElapsedTime: Math.round(sessionElapsedTime),
            sessionStartTime,
            events: sessionTracking.eventHistory,
        };

        trace.info(
            `ExtAddinSessionHistory - Session Summary for controlId:${controlId} - ${JSON.stringify(
                sessionEventData
            )}`
        );

        // Chunk the events array at EVENT BOUNDARIES to ensure each chunk is independently parseable
        // Character budget breakdown:
        // - Base metadata (controlId, properties, timestamps): ~200 chars
        // - Pagination metadata (chunkIndex, totalChunks, sessionId): ~100 chars
        // - Safety margin: 100 chars
        // Available for eventsData: ~600 chars per chunk (conservative estimate)
        const MAX_EVENTS_CHUNK_SIZE = 600;

        // Generate a unique session identifier for chunk reconstruction
        // Using timestamp + controlId hash to ensure uniqueness
        const sessionId = `${Date.now()}-${controlId.substring(0, 8)}`;

        // Split events into chunks at EVENT BOUNDARIES (never mid-event)
        const eventChunks: SessionEventEntry[][] = [];
        let currentChunk: SessionEventEntry[] = [];
        let currentChunkSize = 0;

        for (const event of sessionTracking.eventHistory) {
            const eventJson = JSON.stringify(event);
            const eventSize = eventJson.length + 1; // +1 for comma separator

            // Check if adding this event would exceed the chunk size
            // (but always include at least one event per chunk)
            if (currentChunkSize > 0 && currentChunkSize + eventSize > MAX_EVENTS_CHUNK_SIZE) {
                // Start new chunk
                eventChunks.push(currentChunk);
                currentChunk = [event];
                currentChunkSize = eventSize;
            } else {
                // Add to current chunk
                currentChunk.push(event);
                currentChunkSize += eventSize;
            }
        }

        // Add the last chunk if it has events
        if (currentChunk.length > 0) {
            eventChunks.push(currentChunk);
        }

        const totalChunks = eventChunks.length;

        // Stop logging if chunks exceed maximum limit to prevent excessive telemetry
        if (totalChunks > MAX_CHUNKS_LIMIT) {
            trace.warn(
                `ExtAddinSessionHistory - Skipping session event logging for controlId:${controlId}. Total chunks (${totalChunks}) exceeds limit (${MAX_CHUNKS_LIMIT}). Total events: ${sessionTracking.eventHistory.length}`
            );
            logUsage(
                'ExtAddinSessionHistoryExceededChunkLimit',
                getMailboxInfoForControl(controlId),
                {
                    controlId,
                    totalChunks,
                    totalEventsInSession: sessionTracking.eventHistory.length,
                    sessionElapsedTime: Math.round(sessionElapsedTime),
                    /* eslint-disable-next-line owa-custom-rules/telemetry-no-nested-objects -- (https://aka.ms/OWALintWiki)
                     * Baseline. DO NOT COPY AND PASTE!, replace this line with a justification or fix the issue.
                     *	> Do not call JSON.stringify(...) in logUsage telemetry data. Log flat, scalar custom-data fields instead of stringified or nested data; nested blobs force expensive, bug-prone recursive JSON parsing in the downstream pipeline. */
                    addinProperties: JSON.stringify(controlState.addinProperties),
                }
            );
            return;
        }

        // Common metadata for all chunks
        const baseMetadata = {
            controlId,
            sessionId,
            composeType: controlState.composeType,
            addinProperties: JSON.stringify(controlState.addinProperties),
            totalEventsInSession: sessionTracking.eventHistory.length,
            sessionElapsedTime: Math.round(sessionElapsedTime),
            sessionStartTime,
            totalChunks,
            isSignatureOptimisationV2Enabled: isGlobalFeatureEnabled(
                'addin-signatureOptimisationv2'
            ),
            isAutorunHandleInitEnabled: isGlobalFeatureEnabled('addin-autorunHandleInit'),
        };

        // Helper function to log a single chunk (extracted to avoid loop detection)
        const logChunk = (chunkEvents: SessionEventEntry[], chunkIndex: number) => {
            // Convert chunk to JSON array elements (without outer brackets)
            // This ensures each chunk is independently parseable: [${eventsData}]
            const eventsData = chunkEvents.map(e => JSON.stringify(e)).join(',');

            logUsage('ExtAddinSessionHistory', getMailboxInfoForControl(controlId), {
                ...baseMetadata,
                chunkIndex,
                eventsData,
            });
        };

        // Log each chunk with complete events only
        // Using indexed access pattern to avoid loop-based logUsage detection
        if (totalChunks === 1) {
            logChunk(eventChunks[0], 0);
        } else if (totalChunks === 2) {
            logChunk(eventChunks[0], 0);
            logChunk(eventChunks[1], 1);
        } else {
            // For more than 5 chunks (unlikely with 1000-char limit and event boundaries),
            // use recursion to avoid loop detection
            const logChunksRecursively = (startIndex: number) => {
                if (startIndex < totalChunks) {
                    logChunk(eventChunks[startIndex], startIndex);
                    logChunksRecursively(startIndex + 1);
                }
            };
            logChunksRecursively(0);
        }

        if (totalChunks > 1) {
            trace.info(
                `ExtAddinSessionHistory - Session ${sessionId} split into ${totalChunks} chunks (event-boundary aware) for controlId:${controlId}`
            );
        }
    };

    const endAddinTracking = (
        controlId: string,
        eventName: string,
        eventState: EventState,
        mailboxInfo?: MailboxInfo
    ) => {
        trackEvent(controlId, eventName, eventState);
        if (mailboxInfo && state[controlId]) {
            state[controlId].mailboxInfo = mailboxInfo;
        }
        logAllEventForControlId(controlId);
        logSessionEvents(controlId);
        resetAllEventCountsForControl(controlId);
    };

    // New helper methods for session tracking
    const getSessionEvents = (controlId: string): SessionEventEntry[] => {
        return state[controlId]?.sessionTracking.eventHistory || [];
    };

    const getSessionElapsedTime = (controlId: string): number => {
        const sessionTracking = state[controlId]?.sessionTracking;
        if (!sessionTracking) {
            return 0;
        }
        return performance.now() - sessionTracking.sessionStartTime;
    };

    return {
        trackEvent,
        setAddinProperties,
        setComposeType,
        getEventCounts,
        getAllEventCountsForControl,
        getAllEventCounts,
        resetCounts,
        resetAllEventCountsForControl,
        endAddinTracking,
        getSessionEvents,
        getSessionElapsedTime,
    };
}

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function createTracker called at module scope is not allowed. This may cause unwanted side effects. */
export const addinTracker: Tracker = createTracker();
